import { createQuery, createUrl } from '../helpers'
import {
  DEFAULT_REQUEST_OPTIONS,
  DEFAULT_SINGLE_ITEM_REQUEST_OPTIONS
} from '../config'
import type { ApiClient } from '../../index'
import type { RequestBody } from '../../types'
import type {
  DeepPartial,
  Query,
  RequestOptionsNoQuery,
  PathOptions,
  Path,
  ItemOptions
} from '../types'

/**
 * **Provides CRUD methods for manipulating a specific entity**
 *
 * Also supports a dynamic path via ```pathOptions```
 *
 * @example
 * GET /cs/token
 * POST /cs/token
 * DELETE /cs/token
 */
export const singleItemWrapper = <
  T,
  UpdateBody extends RequestBody = RequestBody,
  CreateBody extends RequestBody = RequestBody,
  PathO extends PathOptions = PathOptions
>(
  apiClient: ApiClient,
  receivedOptions: ItemOptions<PathO>
) => {
  const baseRequestOptions = (() => {
    const mergedOptions = Object.assign(
      {},
      DEFAULT_REQUEST_OPTIONS,
      DEFAULT_SINGLE_ITEM_REQUEST_OPTIONS,
      receivedOptions.requestOptions
    )

    delete mergedOptions.query
    return mergedOptions as Omit<typeof mergedOptions, 'query'>
  })()

  const baseQuery = Object.assign(
    {},
    DEFAULT_REQUEST_OPTIONS.query,
    DEFAULT_SINGLE_ITEM_REQUEST_OPTIONS.query,
    receivedOptions.requestOptions?.query
  )

  const getUrl = (pathOptions?: PathO, requestQuery: Query = {}) =>
    createUrl(receivedOptions.path as Path, requestQuery, pathOptions ?? {})

  const getQuery = (requestQuery: Query) => {
    const mergedQuery = Object.assign({}, baseQuery, requestQuery)

    return createQuery(mergedQuery)
  }

  return {
    get: <U extends DeepPartial<T> = T>(
      requestQuery: Query = {},
      pathOptions?: PathO,
      requestOptions: RequestOptionsNoQuery = {}
    ) => {
      const options = Object.assign({}, baseRequestOptions, requestOptions)

      return apiClient.get<U>(
        getUrl(pathOptions, requestQuery),
        { query: getQuery(requestQuery) },
        { cache: options.cache }
      )
    },

    update: <U extends DeepPartial<T> = T>({
      updates,
      requestQuery = {},
      pathOptions
    }: {
      updates: UpdateBody
      requestQuery?: Query
      pathOptions?: PathO
    }) =>
      apiClient.put<U>(getUrl(pathOptions, requestQuery), {
        body: updates,
        query: getQuery(requestQuery)
      }),

    create: <U extends DeepPartial<T> = T>({
      body,
      requestQuery = {},
      pathOptions
    }: {
      body?: CreateBody
      requestQuery?: Query
      pathOptions?: PathO
    } = {}) =>
      apiClient.post<U>(getUrl(pathOptions, requestQuery), {
        body,
        query: getQuery(requestQuery)
      }),

    remove: <R = any>(pathOptions?: PathO) =>
      apiClient.delete<R>(getUrl(pathOptions))
  }
}

export type SingleItemWrapperPublicInterface = ReturnType<
  typeof singleItemWrapper
>
